/* ALLPOWERS 德国站：太阳能套装年度收益，复用现有 Variant 和产品选项卡。 */
(function () {
  "use strict";

  const ROOT_SELECTOR = "[data-ap-de-solar-yield]";
  const CARD_SELECTOR = "[data-de-option-card]";
  const DEFAULT_ELECTRICITY_PRICE_CENTS = 34.9;
  const ESTIMATE_NOTE =
    "Schätzwerte. Tatsächliche Ergebnisse hängen von Sonneneinstrahlung, Nutzungshäufigkeit, tatsächlichem Stromverbrauch und lokalen Strompreisen ab.";
  const ROOT_CONTROLLERS = new WeakMap();

  const SOLAR_PANEL_WATTS = {
    SP027: 100,
    "SP027+": 100,
    SP029: 140,
    SP033: 200,
    SP035: 200,
    SP037: 400,
    "SP037+": 400,
    SP039: 600,
    SF100: 100,
    SF200: 200,
    SF400: 400,
    SE100: 100,
    SE200: 200
  };

  const CONFIRMED_MODEL_CAPACITY = [
    { pattern: /\bR\s*[- ]?\s*4000\s*[- ]?\s*PLUS\b/i, wattHours: 3456 },
    { pattern: /\bR\s*[- ]?\s*4000\s*[- ]?\s*V2\b/i, wattHours: 2880 },
    { pattern: /\bR\s*[- ]?\s*2500\s*[- ]?\s*PLUS\b/i, wattHours: 2016 },
    { pattern: /\bR\s*[- ]?\s*2500\s*[- ]?\s*V2\b/i, wattHours: 1920 },
    { pattern: /\bBS\s*[- ]?\s*2500(?:\s*[- ]?\s*PRO)?\b/i, wattHours: 2560 },
    { pattern: /\bR\s*[- ]?\s*1500\s*[- ]?\s*LITE\b/i, wattHours: 1056 }
  ];

  function toText(value) {
    if (Array.isArray(value)) return value.filter(Boolean).join(" ");
    if (value === null || value === undefined) return "";
    return String(value);
  }

  function parseDecimal(value) {
    let text = toText(value).trim().replace(/\s/g, "");
    if (!text) return NaN;

    if (/^\d{1,3}(?:\.\d{3})+(?:,\d+)?$/.test(text)) {
      text = text.replace(/\./g, "").replace(",", ".");
    } else if (/^\d{1,3}(?:,\d{3})+(?:\.\d+)?$/.test(text)) {
      text = text.replace(/,/g, "");
    } else {
      text = text.replace(",", ".");
    }

    const number = Number(text);
    return Number.isFinite(number) ? number : NaN;
  }

  function parseCapacity(raw, key) {
    if (raw === null || raw === undefined || raw === "") return 0;

    if (typeof raw === "object") {
      const value = raw.value ?? raw.amount ?? raw.magnitude;
      const unit = raw.unit ?? raw.unit_code ?? key;
      return parseCapacity(value, unit);
    }

    const text = toText(raw).trim();
    const match = text.match(/(\d[\d.,]*)\s*(kwh|wh)?/i);
    if (!match) return 0;

    const value = parseDecimal(match[1]);
    if (!(value > 0)) return 0;

    const unit = toText(match[2] || key).toLowerCase();
    const isKilowattHours = unit.includes("kwh") || (!unit.includes("wh") && value < 30);
    return Math.round(isKilowattHours ? value * 1000 : value);
  }

  function findCapacityInText(source) {
    const text = toText(source);
    if (!text) return 0;

    const labelledBefore = text.match(
      /(?:akku(?:kapazit[aä]t)?|batterie(?:kapazit[aä]t)?|speicher(?:kapazit[aä]t)?|kapazit[aä]t|capacity)[^\d]{0,28}(\d[\d.,]*)\s*(kwh|wh)/i
    );
    if (labelledBefore) return parseCapacity(labelledBefore[1], labelledBefore[2]);

    const labelledAfter = text.match(
      /(\d[\d.,]*)\s*(kwh|wh)\s*(?:akku|batterie|speicher|kapazit[aä]t|capacity)/i
    );
    if (labelledAfter) return parseCapacity(labelledAfter[1], labelledAfter[2]);

    const kilowattHours = text.match(/(\d[\d.,]*)\s*kwh\b/i);
    if (kilowattHours) return parseCapacity(kilowattHours[1], "kwh");

    const wattHours = text.match(/(\d[\d.,]*)\s*wh\b/i);
    if (wattHours) return parseCapacity(wattHours[1], "wh");

    return 0;
  }

  function canonicalPanelModel(value) {
    return toText(value)
      .toUpperCase()
      .replace(/^AP[\s_-]*/, "")
      .replace(/[\s_-]/g, "");
  }

  function escapePattern(value) {
    return toText(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  function findPanelQuantity(source, modelText, panelWatts) {
    const text = toText(source).replace(/×/g, "x");
    const modelPattern = modelText
      ? escapePattern(modelText).replace(/[\s_-]+/g, "[\\s_-]*")
      : "";
    const wattsPattern = panelWatts ? `${escapePattern(panelWatts)}\\s*w(?:att)?` : "";
    const patterns = [];

    if (modelPattern) {
      patterns.push(new RegExp(`(\\d{1,2})\\s*[x*]\\s*(?:AP[\\s_-]*)?${modelPattern}`, "i"));
      patterns.push(new RegExp(`${modelPattern}\\s*[x*]\\s*(\\d{1,2})`, "i"));
    }

    if (wattsPattern) {
      patterns.push(new RegExp(`(\\d{1,2})\\s*[x*]\\s*${wattsPattern}`, "i"));
      patterns.push(new RegExp(`${wattsPattern}\\s*[x*]\\s*(\\d{1,2})`, "i"));
    }

    for (const pattern of patterns) {
      const match = text.match(pattern);
      if (!match) continue;
      const count = Number(match[1]);
      if (Number.isInteger(count) && count > 0 && count <= 20) return count;
    }

    return 1;
  }

  function readSolarPanel(source) {
    const text = toText(source);
    if (!text || /^default title$/i.test(text.trim())) return null;

    const knownModelPattern = /\b(?:AP[\s_-]*)?(?:SP[\s_-]*0(?:27\+?|29|33|35|37\+?|39)|SF[\s_-]*(?:100|200|400)|SE[\s_-]*(?:100|200))\b/gi;
    let knownModel;

    while ((knownModel = knownModelPattern.exec(text)) !== null) {
      const model = canonicalPanelModel(knownModel[0]);
      const watts = SOLAR_PANEL_WATTS[model];
      if (!watts) continue;

      return {
        model,
        watts,
        quantity: findPanelQuantity(text, knownModel[0], watts)
      };
    }

    const contextualPatterns = [
      /(?:solarpanels?|solarmodule?|solar\s*panels?|solar\s*modules?|pv[\s-]*module?)[^\d]{0,16}(\d{2,4})\s*w\b/i,
      /(\d{2,4})\s*w\b[^\n]{0,24}(?:solarpanels?|solarmodule?|solar\s*panels?|solar\s*modules?|pv[\s-]*module?)/i,
      /(?:\+|\bmit\b|\bplus\b)\s*(?:\d{1,2}\s*[x×*]\s*)?(\d{2,4})\s*w\b/i,
      /\b\d{1,2}\s*[x×*]\s*(\d{2,4})\s*w\b/i
    ];

    for (const pattern of contextualPatterns) {
      const match = text.match(pattern);
      if (!match) continue;

      const watts = Number(match[1]);
      if (!(watts >= 20 && watts <= 1200)) continue;

      return {
        model: "",
        watts,
        quantity: findPanelQuantity(text, "", watts)
      };
    }

    return null;
  }

  function hasPowerStation(source) {
    return /\b(?:BS|R|P|S)\s*[- ]?\s*\d{3,4}(?:\s*[- ]?\s*(?:V2|PLUS|PRO|LITE))?\b/i.test(
      toText(source)
    );
  }

  function getProductCategory(sources) {
    for (const source of sources) {
      const text = toText(source);

      if (/\bBS\s*[- ]?\s*\d{3,5}(?:\s*[- ]?\s*(?:PRO|PLUS|V2))?\b/i.test(text)) {
        return "balcony_storage";
      }

      if (/\b(?:R|P|S)\s*[- ]?\s*\d{3,4}(?:\s*[- ]?\s*(?:V2|PLUS|PRO|LITE))?\b/i.test(text)) {
        return "portable_powerstation";
      }
    }

    const combined = sources.map(toText).join(" ");
    return /balkonkraftwerk|balkonspeicher|balkon[-\s]?speicher|heimspeicher/i.test(combined)
      ? "balcony_storage"
      : "portable_powerstation";
  }

  function readKnownModelCapacity(source) {
    const text = toText(source);
    for (const entry of CONFIRMED_MODEL_CAPACITY) {
      if (entry.pattern.test(text)) return entry.wattHours;
    }
    return 0;
  }

  function parseManifest(root) {
    const script = root.querySelector("[data-ap-de-solar-yield-data]");
    if (!script) return null;

    try {
      const manifest = JSON.parse(script.textContent || "{}");
      if (!manifest.product || !Array.isArray(manifest.variants)) return null;
      return manifest;
    } catch (error) {
      console.error("ALLPOWERS solar yield: invalid product data", error);
      return null;
    }
  }

  function getScope(root) {
    return root.closest(".shopify-section") || root.closest(".product__info-container") || document;
  }

  function getCurrentVariantId(root, preferredId) {
    if (preferredId) return String(preferredId);

    const sectionId = root.dataset.sectionId;
    const productForm = document.getElementById(`productForm-${sectionId}`);
    const formInput = productForm && productForm.querySelector('[name="id"]');
    if (formInput && formInput.value) return String(formInput.value);

    const scope = getScope(root);
    const selectedCard = scope.querySelector(`${CARD_SELECTOR}.is-selected`);
    if (selectedCard && selectedCard.dataset.variantId) {
      return String(selectedCard.dataset.variantId);
    }

    return String(root.dataset.selectedVariantId || "");
  }

  function getCurrentCard(root, variantId) {
    const scope = getScope(root);
    return Array.from(scope.querySelectorAll(CARD_SELECTOR)).find(
      (card) => String(card.dataset.variantId || "") === String(variantId)
    );
  }

  function readRenderedCapacity(scope) {
    const labels = scope.querySelectorAll(
      ".product-spec_name, .specification-item, [data-product-specification], [data-capacity]"
    );

    for (const item of labels) {
      const text = toText(item.textContent);
      if (!/(?:akku|batterie|speicher|kapazit[aä]t|capacity)/i.test(text)) continue;

      const capacity = findCapacityInText(text);
      if (capacity > 0) return capacity;

      const next = item.nextElementSibling;
      if (next) {
        const nextCapacity = findCapacityInText(next.textContent);
        if (nextCapacity > 0) return nextCapacity;
      }
    }

    return 0;
  }

  function resolveCapacity(manifest, variant, stationSources, scope) {
    const variantCapacity = parseCapacity(variant.capacity?.value, variant.capacity?.key);
    if (variantCapacity > 0) return variantCapacity;

    for (const source of stationSources.slice(0, 2)) {
      const selectedModelCapacity = readKnownModelCapacity(source);
      if (selectedModelCapacity > 0) return selectedModelCapacity;
    }

    const productCapacity = parseCapacity(
      manifest.product.capacity?.value,
      manifest.product.capacity?.key
    );
    if (productCapacity > 0) return productCapacity;

    const verifiedModelCapacity = readKnownModelCapacity(stationSources[2]);
    if (verifiedModelCapacity > 0) return verifiedModelCapacity;

    for (const source of [variant.title, toText(variant.options), manifest.product.title]) {
      const capacity = findCapacityInText(source);
      if (capacity > 0) return capacity;
    }

    const renderedCapacity = readRenderedCapacity(scope);
    if (renderedCapacity > 0) return renderedCapacity;

    return findCapacityInText(manifest.product.description);
  }

  function formatInteger(value) {
    return new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 }).format(value);
  }

  function formatKilowattHours(wattHours) {
    return new Intl.NumberFormat("de-DE", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3
    }).format(wattHours / 1000);
  }

  function formatElectricityPrice(priceCents) {
    return new Intl.NumberFormat("de-DE", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(priceCents);
  }

  function setValue(target, text) {
    if (!target) return;
    target.textContent = text;
  }

  function openInfoDialog(root) {
    const dialog = root.querySelector("[data-ap-de-solar-yield-dialog]");
    const trigger = root.querySelector("[data-ap-de-solar-yield-info]");
    if (!dialog || !trigger || dialog.open) return;

    if (typeof dialog.showModal === "function") {
      dialog.showModal();
    } else {
      dialog.setAttribute("open", "");
    }

    trigger.setAttribute("aria-expanded", "true");
    const closeButton = root.querySelector("[data-ap-de-solar-yield-close]");
    if (closeButton && typeof closeButton.focus === "function") {
      closeButton.focus({ preventScroll: true });
    }
  }

  function closeInfoDialog(root, restoreFocus = true) {
    const dialog = root.querySelector("[data-ap-de-solar-yield-dialog]");
    const trigger = root.querySelector("[data-ap-de-solar-yield-info]");
    if (!dialog || !trigger) return;

    if (dialog.open) {
      if (typeof dialog.close === "function") {
        dialog.close();
      } else {
        dialog.removeAttribute("open");
      }
    }

    trigger.setAttribute("aria-expanded", "false");
    if (restoreFocus && typeof trigger.focus === "function") {
      trigger.focus({ preventScroll: true });
    }
  }

  function hideModule(root) {
    closeInfoDialog(root, false);
    root.hidden = true;
  }

  function updateModule(root, manifest, preferredVariantId) {
    const variantId = getCurrentVariantId(root, preferredVariantId);
    const variant = manifest.variants.find((item) => String(item.id) === variantId);
    if (!variant) {
      hideModule(root);
      return;
    }

    const card = getCurrentCard(root, variantId);
    const variantText = [variant.title, toText(variant.options), variant.sku]
      .filter(Boolean)
      .join(" ");
    const cardText = card ? [card.dataset.cardTitle, card.textContent].filter(Boolean).join(" ") : "";
    const productText = [manifest.product.title, manifest.product.handle, manifest.product.type]
      .filter(Boolean)
      .join(" ");
    const stationText = [variantText, cardText, productText].filter(Boolean).join(" ");

    if (!hasPowerStation(stationText)) {
      hideModule(root);
      return;
    }

    let panel = null;
    for (const source of [variantText, cardText, productText]) {
      panel = readSolarPanel(source);
      if (panel) break;
    }

    if (!panel || !(panel.watts > 0) || !(panel.quantity > 0)) {
      hideModule(root);
      return;
    }

    const capacity = resolveCapacity(
      manifest,
      variant,
      [variantText, cardText, productText],
      getScope(root)
    );
    if (!(capacity > 0)) {
      hideModule(root);
      return;
    }

    const annualFactor = Math.max(0, parseDecimal(root.dataset.annualYieldFactor) || 900);
    const usageRate = Math.max(0, Math.min(100, parseDecimal(root.dataset.usageRate) || 100));
    const parsedPrice = parseDecimal(root.dataset.electricityPriceCents);
    const electricityPrice = parsedPrice > 0 ? parsedPrice : DEFAULT_ELECTRICITY_PRICE_CENTS;
    const annualYield = Math.round((panel.watts * panel.quantity / 1000) * annualFactor * (usageRate / 100));
    const annualValue = Math.round(annualYield * electricityPrice / 100);
    const productCategory = getProductCategory([variantText, cardText, productText]);
    const isBalconyStorage = productCategory === "balcony_storage";
    const valueLabel = isBalconyStorage ? "Ersparnis/Jahr" : "Solarstromwert/Jahr";
    const valueDescription = isBalconyStorage
      ? `geschätzte jährliche Stromkostenersparnis: ${formatInteger(annualValue)} €`
      : `geschätzter jährlicher Wert des erzeugten Solarstroms: ${formatInteger(annualValue)} €`;
    const valueLabelTarget = root.querySelector("[data-ap-de-solar-yield-value-label]");
    const dialogPrice = root.querySelector("[data-ap-de-solar-yield-dialog-price]");
    const dialogNote = root.querySelector("[data-ap-de-solar-yield-dialog-note]");

    if (valueLabelTarget) valueLabelTarget.textContent = valueLabel;
    if (dialogPrice) {
      dialogPrice.textContent = `Referenzstrompreis 2026: ${formatElectricityPrice(electricityPrice)} ct/kWh`;
    }
    if (dialogNote) {
      dialogNote.textContent = isBalconyStorage
        ? "Die tatsächliche Ersparnis hängt davon ab, wie viel erzeugter Solarstrom selbst genutzt wird."
        : "Der Solarstromwert ist eine Schätzung und keine zugesicherte jährliche Ersparnis bei den Haushaltsstromkosten.";
    }
    root.setAttribute("data-product-category", productCategory);

    setValue(root.querySelector("[data-ap-de-solar-yield-annual]"), `${formatInteger(annualYield)} kWh`);
    setValue(root.querySelector("[data-ap-de-solar-yield-capacity]"), `${formatKilowattHours(capacity)} kWh`);
    setValue(root.querySelector("[data-ap-de-solar-yield-savings]"), `${formatInteger(annualValue)} €`);

    const information = [
      `Geschätzter Solarertrag: ${formatInteger(annualYield)} kWh pro Jahr`,
      `Akkukapazität: ${formatKilowattHours(capacity)} kWh`,
      valueDescription,
      `Referenzstrompreis Deutschland 2026: ${formatElectricityPrice(electricityPrice)} Cent pro kWh`,
      ESTIMATE_NOTE
    ].join(". ");

    root.setAttribute("aria-label", information);
    root.hidden = false;
  }

  function initialize(root) {
    if (ROOT_CONTROLLERS.has(root)) return;

    const manifest = parseManifest(root);
    if (!manifest) return;

    const controller = new AbortController();
    ROOT_CONTROLLERS.set(root, controller);
    const scope = getScope(root);
    const options = { signal: controller.signal };
    const dialog = root.querySelector("[data-ap-de-solar-yield-dialog]");

    root.addEventListener(
      "click",
      (event) => {
        if (!event.target || typeof event.target.closest !== "function") return;

        if (event.target.closest("[data-ap-de-solar-yield-info]")) {
          event.preventDefault();
          openInfoDialog(root);
          return;
        }

        if (event.target.closest("[data-ap-de-solar-yield-close]")) {
          event.preventDefault();
          closeInfoDialog(root);
        }
      },
      options
    );

    if (dialog) {
      dialog.addEventListener(
        "click",
        (event) => {
          if (event.target === dialog) closeInfoDialog(root);
        },
        options
      );

      dialog.addEventListener(
        "cancel",
        (event) => {
          event.preventDefault();
          closeInfoDialog(root);
        },
        options
      );

      dialog.addEventListener(
        "close",
        () => {
          const trigger = root.querySelector("[data-ap-de-solar-yield-info]");
          if (trigger) trigger.setAttribute("aria-expanded", "false");
        },
        options
      );
    }

    scope.addEventListener(
      "de:product-option:change",
      (event) => {
        const origin = event.target.closest("[data-de-product-options]");
        if (origin && origin.dataset.sectionId !== root.dataset.sectionId) return;
        updateModule(root, manifest, event.detail?.variantId);
      },
      options
    );

    scope.addEventListener(
      "change",
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const isVariantId = target.matches('[name="id"]');
        const isVariantOption = target.matches("[data-de-option-input], .js-variant-radio-item");
        if (!isVariantId && !isVariantOption) return;

        const nextId = isVariantId ? target.value : "";
        requestAnimationFrame(() => updateModule(root, manifest, nextId));
      },
      options
    );

    updateModule(root, manifest);
  }

  function initializeAll(scope) {
    const context = scope && scope.querySelectorAll ? scope : document;
    if (context.matches && context.matches(ROOT_SELECTOR)) initialize(context);
    context.querySelectorAll(ROOT_SELECTOR).forEach(initialize);
  }

  function disposeAll(scope) {
    const context = scope && scope.querySelectorAll ? scope : document;
    const roots = Array.from(context.querySelectorAll(ROOT_SELECTOR));
    if (context.matches && context.matches(ROOT_SELECTOR)) roots.push(context);

    roots.forEach((root) => {
      const controller = ROOT_CONTROLLERS.get(root);
      if (!controller) return;
      closeInfoDialog(root, false);
      controller.abort();
      ROOT_CONTROLLERS.delete(root);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initializeAll(document), { once: true });
  } else {
    initializeAll(document);
  }

  document.addEventListener("shopify:section:load", (event) => initializeAll(event.target));
  document.addEventListener("shopify:section:unload", (event) => disposeAll(event.target));
})();